"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useUserContext } from "@/app/shared/user";
import { ActiveFiltersView } from "../models/filters";
import { LISTING_ROUTES } from "../constants/routes";
import {
	LISTING_FILTER_PARAM_KEYS,
	applyFilterParams,
} from "../utils/filter-url-params";

const STORAGE_KEY_PREFIX = "infinit-listing-filters";
const URL_FILTER_KEYS = [...LISTING_FILTER_PARAM_KEYS, "savedSearchId"];

type UpdateFilters = (
	newFilters: (prev: ActiveFiltersView) => ActiveFiltersView
) => void;

function serialize(filters: ActiveFiltersView): string {
	const params = new URLSearchParams();
	const filterParams = filters.toURLParams();
	delete filterParams.searchText;
	applyFilterParams(params, filterParams);
	return params.toString();
}

function readStored(key: string): string | null {
	if (typeof window === "undefined") return null;
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function writeStored(key: string, value: string): void {
	if (typeof window === "undefined") return;
	try {
		if (value) {
			localStorage.setItem(key, value);
		} else {
			localStorage.removeItem(key);
		}
	} catch {
		// localStorage unavailable (e.g. private browsing) — filters still work in-session
	}
}

function hasFiltersBeyondMarketsAndSearch(filters: ActiveFiltersView): boolean {
	const params = filters.toURLParams();
	delete params.markets;
	delete params.searchText;
	return Object.keys(params).length > 0;
}

export function usePersistedListingFilters(
	activeFilters: ActiveFiltersView,
	updateFilters: UpdateFilters
) {
	const userContext = useUserContext();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const userId = userContext?.id;
	const orgId = userContext?.currentOrganizationId;
	const storageKey =
		userId && orgId ? `${STORAGE_KEY_PREFIX}:${userId}:${orgId}` : null;

	const decidedKeyRef = useRef<string | null>(null);
	const pendingRestoreRef = useRef(false);
	const baselineRef = useRef<string | null>(null);

	const isListingRoute = pathname === LISTING_ROUTES.MAIN;

	const hasUrlFilters = useCallback(
		() => URL_FILTER_KEYS.some(key => searchParams.has(key)),
		[searchParams]
	);

	useEffect(() => {
		if (
			!storageKey ||
			!isListingRoute ||
			decidedKeyRef.current === storageKey
		) {
			return;
		}
		decidedKeyRef.current = storageKey;
		pendingRestoreRef.current = false;
		baselineRef.current = serialize(activeFilters);

		if (hasUrlFilters() || hasFiltersBeyondMarketsAndSearch(activeFilters)) {
			return;
		}

		const stored = readStored(storageKey);
		if (!stored) return;
		const restored = ActiveFiltersView.fromQueryParams(
			new URLSearchParams(stored)
		);
		restored.setSearchText(undefined);
		if (
			!hasFiltersBeyondMarketsAndSearch(restored) &&
			!restored.markets.length
		) {
			return;
		}

		pendingRestoreRef.current = true;
		baselineRef.current = null;
		updateFilters(prev => {
			if (restored.markets.length === 0 && prev.markets.length > 0) {
				restored.setMarkets(prev.markets);
			}
			restored.setSearchText(prev.searchText);
			return restored;
		});
	}, [storageKey, isListingRoute, activeFilters, hasUrlFilters, updateFilters]);

	useEffect(() => {
		if (
			!storageKey ||
			!isListingRoute ||
			decidedKeyRef.current !== storageKey
		) {
			return;
		}
		if (pendingRestoreRef.current) {
			if (!activeFilters.hasAppliedFilters()) return;
			pendingRestoreRef.current = false;
		}
		const serialized = serialize(activeFilters);
		if (baselineRef.current !== null) {
			if (serialized === baselineRef.current) return;
			baselineRef.current = null;
		}
		writeStored(storageKey, serialized);
	}, [storageKey, isListingRoute, activeFilters]);
}
